import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';

import { TeamTaskCreateReq, teamApi } from '../api';
import { TEAM_ASSIGNED_TASKS_QUERY_KEY, TEAM_TASKS_QUERY_KEY } from '../queries';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface CreateTeamTaskParams {
  teamId: string;
  boardId: string;
  body: TeamTaskCreateReq;
  showSnackbar?: boolean;
}

export const useCreateTeamTask = () => {
  const queryClient = useQueryClient();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);

  const createTeamTaskMutation = useMutation({
    mutationFn: ({ teamId, boardId, body }: CreateTeamTaskParams) => {
      return teamApi.createTeamTask(teamId, boardId, body).then(({ data }) => data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY });

      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.created'), type: 'success', icon: 'task' });
      }
    },
    onError: (_error, variables) => {
      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.createFailed'), type: 'error', icon: 'task' });
      }
    },
  });

  return {
    createTeamTask: createTeamTaskMutation.mutateAsync,
    isCreatingTeamTask: createTeamTaskMutation.isPending,
    ...createTeamTaskMutation,
  };
};
